import { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Camera, Calendar, Download, MessageCircle, Image as ImageIcon, 
  Send, FileText, CheckCircle, XCircle, Loader2, Clock, 
  CreditCard, FileSignature, ChevronRight, Mail, RefreshCw,
  ExternalLink, Eye
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";
import BrandingFooter from "../components/BrandingFooter";
import FloatingBrandingBadge from "../components/FloatingBrandingBadge";

const EnhancedClientPortal = () => {
  const { sessionToken } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("bookings");
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchPortalData();
  }, [sessionToken]);

  const fetchPortalData = async () => {
    try {
      const response = await axios.get(`${API}/portal/session/${sessionToken}`);
      setData(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Session expired. Please request a new access link.");
        navigate("/client-login");
      } else {
        toast.error("Invalid or expired session");
        navigate("/client-login");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshSession = async () => {
    setRefreshing(true);
    try {
      await axios.post(`${API}/portal/session/${sessionToken}/refresh`);
      toast.success("Session extended for 24 hours");
      fetchPortalData();
    } catch (error) {
      toast.error("Failed to extend session");
    } finally {
      setRefreshing(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
      confirmed: "bg-green-500/20 text-green-400 border-green-500/30",
      declined: "bg-red-500/20 text-red-400 border-red-500/30",
      completed: "bg-purple-500/20 text-purple-400 border-purple-500/30",
      in_discussion: "bg-blue-500/20 text-blue-400 border-blue-500/30",
      quote_sent: "bg-orange-500/20 text-orange-400 border-orange-500/30",
      quote_declined: "bg-red-500/20 text-red-400 border-red-500/30",
      // Invoice statuses
      draft: "bg-gray-500/20 text-gray-400 border-gray-500/30",
      sent: "bg-blue-500/20 text-blue-400 border-blue-500/30",
      paid: "bg-green-500/20 text-green-400 border-green-500/30",
      overdue: "bg-red-500/20 text-red-400 border-red-500/30",
      // Contract statuses
      signed: "bg-green-500/20 text-green-400 border-green-500/30"
    };
    return styles[status] || styles.pending;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD"
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="text-center">
          <h1 className="text-2xl mb-4">Session Not Found</h1>
          <p className="text-muted-foreground mb-6">This session may have expired or is invalid.</p>
          <Link to="/client-login">
            <Button>Request New Access</Button>
          </Link>
        </div>
      </div>
    );
  }

  const { photographer, bookings, galleries, invoices, contracts, client_email, session_expires_at } = data;
  
  // Dynamic branding
  const brandName = photographer?.company_name || "360Lumina";
  const logoUrl = photographer?.logo_url ? `${API.replace("/api", "")}${photographer.logo_url}` : null;

  return (
    <div className="min-h-screen py-8 px-4 md:px-6" data-testid="enhanced-client-portal">
      {/* Header */}
      <div className="max-w-6xl mx-auto mb-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            {logoUrl ? (
              <img src={logoUrl} alt={brandName} className="h-10 max-w-[160px] object-contain" />
            ) : (
              <>
                <Camera className="w-10 h-10 text-primary" />
                <span className="text-2xl font-serif">{brandName}</span>
              </>
            )}
          </div>
          
          <div className="flex items-center gap-3">
            <div className="text-sm text-muted-foreground">
              <Mail className="w-4 h-4 inline mr-1" />
              {client_email}
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleRefreshSession}
              disabled={refreshing}
            >
              {refreshing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              <span className="ml-2 hidden sm:inline">Extend Session</span>
            </Button>
          </div>
        </div>
        
        <div>
          <h1 className="text-3xl md:text-4xl tracking-tight mb-2">Your Client Portal</h1>
          <p className="text-muted-foreground">
            View all your sessions, invoices, contracts, and galleries with {photographer?.user?.name || brandName}
          </p>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="max-w-6xl mx-auto mb-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Calendar className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{bookings?.length || 0}</p>
                  <p className="text-sm text-muted-foreground">Bookings</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-500/10 rounded-lg">
                  <ImageIcon className="w-5 h-5 text-green-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{galleries?.length || 0}</p>
                  <p className="text-sm text-muted-foreground">Galleries</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/10 rounded-lg">
                  <CreditCard className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{invoices?.length || 0}</p>
                  <p className="text-sm text-muted-foreground">Invoices</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-500/10 rounded-lg">
                  <FileSignature className="w-5 h-5 text-purple-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{contracts?.length || 0}</p>
                  <p className="text-sm text-muted-foreground">Contracts</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Main Content Tabs */}
      <div className="max-w-6xl mx-auto">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="bookings" data-testid="tab-bookings">
              <Calendar className="w-4 h-4 mr-2" />
              Bookings
            </TabsTrigger>
            <TabsTrigger value="galleries" data-testid="tab-galleries">
              <ImageIcon className="w-4 h-4 mr-2" />
              Galleries
            </TabsTrigger>
            <TabsTrigger value="invoices" data-testid="tab-invoices">
              <CreditCard className="w-4 h-4 mr-2" />
              Invoices
            </TabsTrigger>
            <TabsTrigger value="contracts" data-testid="tab-contracts">
              <FileSignature className="w-4 h-4 mr-2" />
              Contracts
            </TabsTrigger>
          </TabsList>

          {/* Bookings Tab */}
          <TabsContent value="bookings">
            {bookings?.length === 0 ? (
              <Card className="bg-card border-border">
                <CardContent className="py-12 text-center">
                  <Calendar className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <p className="text-lg mb-2">No bookings yet</p>
                  <p className="text-muted-foreground">Your booking history will appear here</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {bookings.map((booking, index) => (
                  <motion.div
                    key={booking.request_id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Card className="bg-card border-border hover:border-primary/30 transition-colors">
                      <CardContent className="p-6">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="text-lg font-medium">{booking.event_type}</h3>
                              <Badge className={`${getStatusBadge(booking.status)} border`}>
                                {booking.status.replace("_", " ")}
                              </Badge>
                            </div>
                            
                            <div className="grid sm:grid-cols-3 gap-4 text-sm">
                              <div>
                                <span className="text-muted-foreground">Date:</span>{" "}
                                <span className="text-foreground">
                                  {booking.is_tournament 
                                    ? `${booking.tournament_start_date} - ${booking.tournament_end_date}`
                                    : booking.event_date || "TBD"}
                                </span>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Services:</span>{" "}
                                <span className="text-foreground">{booking.request_types?.join(", ")}</span>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Venue:</span>{" "}
                                <span className="text-foreground">{booking.venue_name || "TBD"}</span>
                              </div>
                            </div>
                            
                            {booking.quote && (
                              <div className="mt-3 p-3 bg-muted/30 rounded-lg">
                                <div className="flex items-center justify-between">
                                  <span className="text-sm">
                                    <FileText className="w-4 h-4 inline mr-1" />
                                    Quote: {booking.quote.package_name}
                                  </span>
                                  <span className="font-medium">{formatCurrency(booking.quote.total)}</span>
                                </div>
                              </div>
                            )}
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <Link to={`/portal/${booking.magic_link_token}`}>
                              <Button variant="outline" size="sm">
                                <Eye className="w-4 h-4 mr-2" />
                                View Details
                              </Button>
                            </Link>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Galleries Tab */}
          <TabsContent value="galleries">
            {galleries?.length === 0 ? (
              <Card className="bg-card border-border">
                <CardContent className="py-12 text-center">
                  <ImageIcon className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <p className="text-lg mb-2">No galleries yet</p>
                  <p className="text-muted-foreground">Your delivered galleries will appear here</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {galleries.map((gallery, index) => (
                  <motion.div
                    key={gallery.gallery_id}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Card className="bg-card border-border overflow-hidden hover:border-primary/30 transition-colors">
                      <div className="aspect-video bg-muted relative">
                        {gallery.cover_image ? (
                          <img 
                            src={`${API}/media/${gallery.gallery_id}/${gallery.cover_image}?token=${gallery.magic_link_token}`}
                            alt={gallery.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <ImageIcon className="w-12 h-12 text-muted-foreground/30" />
                          </div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                        <div className="absolute bottom-3 left-3 right-3">
                          <h3 className="text-white font-medium truncate">{gallery.title}</h3>
                        </div>
                      </div>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between text-sm mb-3">
                          <span className="text-muted-foreground">
                            {formatDate(gallery.event_date || gallery.created_at)}
                          </span>
                          {gallery.expires_at && (
                            <Badge variant="outline" className="text-xs">
                              Expires {formatDate(gallery.expires_at)}
                            </Badge>
                          )}
                        </div>
                        <Link to={`/portal/${gallery.magic_link_token}`}>
                          <Button className="w-full" size="sm">
                            <ImageIcon className="w-4 h-4 mr-2" />
                            View Gallery
                          </Button>
                        </Link>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Invoices Tab */}
          <TabsContent value="invoices">
            {invoices?.length === 0 ? (
              <Card className="bg-card border-border">
                <CardContent className="py-12 text-center">
                  <CreditCard className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <p className="text-lg mb-2">No invoices yet</p>
                  <p className="text-muted-foreground">Your invoices and payment history will appear here</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {invoices.map((invoice, index) => (
                  <motion.div
                    key={invoice.invoice_id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Card className="bg-card border-border hover:border-primary/30 transition-colors">
                      <CardContent className="p-6">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="text-lg font-medium">
                                Invoice #{invoice.invoice_number || invoice.invoice_id?.slice(-8)}
                              </h3>
                              <Badge className={`${getStatusBadge(invoice.status)} border`}>
                                {invoice.status}
                              </Badge>
                            </div>
                            
                            <div className="grid sm:grid-cols-3 gap-4 text-sm">
                              <div>
                                <span className="text-muted-foreground">Amount:</span>{" "}
                                <span className="text-foreground font-medium">{formatCurrency(invoice.total)}</span>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Date:</span>{" "}
                                <span className="text-foreground">{formatDate(invoice.created_at)}</span>
                              </div>
                              {invoice.due_date && (
                                <div>
                                  <span className="text-muted-foreground">Due:</span>{" "}
                                  <span className="text-foreground">{formatDate(invoice.due_date)}</span>
                                </div>
                              )}
                            </div>
                            
                            {invoice.description && (
                              <p className="mt-2 text-sm text-muted-foreground">{invoice.description}</p>
                            )}
                          </div>
                          
                          <div className="flex items-center gap-2">
                            {invoice.status !== "paid" && invoice.payment_link && (
                              <a href={invoice.payment_link} target="_blank" rel="noopener noreferrer">
                                <Button size="sm">
                                  <CreditCard className="w-4 h-4 mr-2" />
                                  Pay Now
                                </Button>
                              </a>
                            )}
                            {invoice.stripe_invoice_url && (
                              <a href={invoice.stripe_invoice_url} target="_blank" rel="noopener noreferrer">
                                <Button variant="outline" size="sm">
                                  <ExternalLink className="w-4 h-4 mr-2" />
                                  View Invoice
                                </Button>
                              </a>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Contracts Tab */}
          <TabsContent value="contracts">
            {contracts?.length === 0 ? (
              <Card className="bg-card border-border">
                <CardContent className="py-12 text-center">
                  <FileSignature className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <p className="text-lg mb-2">No contracts yet</p>
                  <p className="text-muted-foreground">Your contracts and agreements will appear here</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {contracts.map((contract, index) => (
                  <motion.div
                    key={contract.contract_id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Card className="bg-card border-border hover:border-primary/30 transition-colors">
                      <CardContent className="p-6">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="text-lg font-medium">
                                {contract.template_name || "Service Agreement"}
                              </h3>
                              <Badge className={`${getStatusBadge(contract.status)} border`}>
                                {contract.status}
                              </Badge>
                            </div>
                            
                            <div className="grid sm:grid-cols-3 gap-4 text-sm">
                              <div>
                                <span className="text-muted-foreground">Sent:</span>{" "}
                                <span className="text-foreground">{formatDate(contract.sent_at)}</span>
                              </div>
                              {contract.signed_at && (
                                <div>
                                  <span className="text-muted-foreground">Signed:</span>{" "}
                                  <span className="text-foreground">{formatDate(contract.signed_at)}</span>
                                </div>
                              )}
                              {contract.booking_request_id && (
                                <div>
                                  <span className="text-muted-foreground">Booking:</span>{" "}
                                  <span className="text-foreground">#{contract.booking_request_id.slice(-8)}</span>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            {contract.status === "sent" && (
                              <Link to={`/contract/sign/${contract.contract_id}`}>
                                <Button size="sm">
                                  <FileSignature className="w-4 h-4 mr-2" />
                                  Sign Now
                                </Button>
                              </Link>
                            )}
                            {contract.status === "signed" && (
                              <Button variant="outline" size="sm" disabled>
                                <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                                Signed
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Footer */}
      <div className="max-w-6xl mx-auto mt-12 pt-6 border-t border-border">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <p>Session expires: {formatDate(session_expires_at)}</p>
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
            <BrandingFooter planKey={data?.photographer_plan_key} />
          </div>
        </div>
      </div>

      {/* Floating branding badge for Starter/Pro tiers */}
      <FloatingBrandingBadge planKey={data?.photographer_plan_key} />
    </div>
  );
};

export default EnhancedClientPortal;
